// The Access page (ADR 0066): the app's admins manage its people (invite, resend, role, remove), each person's level
// (all tenants or one tenant) and the tenants. A person who is not an admin is told only admins manage people.
import type { components } from '@cortex/sdk-access-client';
import { Badge, Button, Input } from '@cortex/ui';
import * as React from 'react';
import { errorMessage, useAccessHost } from './host.tsx';

type Person = components['schemas']['AccessPerson'];
type Tenant = components['schemas']['AccessTenant'];
type Role = Person['role'];

const ROLES: { value: Role; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'member', label: 'Member' },
  { value: 'viewer', label: 'Viewer' },
];
const STATUS: Record<Person['status'], string> = {
  invited: 'Invited',
  expired: 'Invitation expired',
  active: 'Active',
  reset_required: 'Must reset password',
  disabled: 'Disabled',
};
const ALL = '*';
const NONE = '';
const selectClass =
  'h-9 rounded-md border border-input bg-background px-2 text-sm shadow-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';
const idOf = (email: string) => email.replace(/[^a-z0-9]+/gi, '-');

/** The value of a level select: all tenants, one tenant, or none. */
const levelValue = (p: Pick<Person, 'allTenants' | 'tenant'>) =>
  p.allTenants ? ALL : (p.tenant?.id ?? NONE);
const levelBody = (v: string) =>
  v === ALL ? { allTenants: true } : v ? { allTenants: false, tenantId: v } : {};

function LevelSelect(props: {
  value: string;
  tenants: Tenant[];
  disabled?: boolean;
  onChange(v: string): void;
  testId: string;
  label: string;
}) {
  return (
    <select
      aria-label={props.label}
      data-testid={props.testId}
      className={selectClass}
      value={props.value}
      disabled={props.disabled}
      onChange={(e) => props.onChange(e.target.value)}
    >
      <option value={NONE}>No tenant</option>
      <option value={ALL}>All tenants</option>
      {props.tenants.map((t) => (
        <option key={t.id} value={t.id}>
          {t.name}
        </option>
      ))}
    </select>
  );
}

export function AccessPage() {
  const { client, scope } = useAccessHost();
  const [people, setPeople] = React.useState<Person[] | null>(null);
  const [tenants, setTenants] = React.useState<Tenant[]>([]);
  const [directory, setDirectory] = React.useState<{ available: boolean; note: string | null }>({
    available: true,
    note: null,
  });
  const [linked, setLinked] = React.useState(false);
  const [denied, setDenied] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [invite, setInvite] = React.useState({ email: '', role: 'member' as Role, level: NONE });
  const [tenantName, setTenantName] = React.useState('');
  const [renaming, setRenaming] = React.useState<{ id: string; name: string } | null>(null);
  const [removing, setRemoving] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    const [p, t] = await Promise.all([client.GET('/people'), client.GET('/tenants')]);
    if (p.error || t.error) {
      const res = p.error ? p.response : t.response;
      if (res.status === 403 || res.status === 404)
        setDenied(errorMessage(p.error ?? t.error, res) || 'Only the app’s admins manage its people.');
      else setError(errorMessage(p.error ?? t.error, res));
      setPeople([]);
      return;
    }
    setDenied(null);
    setPeople(p.data.people);
    setDirectory(p.data.directory);
    setLinked(p.data.allTenantsRole === 'admin');
    setTenants(t.data.tenants);
  }, [client]);

  React.useEffect(() => {
    setPeople(null);
    void load();
  }, [load, scope]);

  /** One change: busy while it runs, its error shown, the lists read again after. */
  const act = async (what: () => Promise<{ error?: unknown; response: Response }>, done?: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const r = await what();
      if (r.error) setError(errorMessage(r.error, r.response));
      else if (done) setNotice(done);
      await load();
      return !r.error;
    } finally {
      setBusy(false);
    }
  };

  if (denied)
    return (
      <div className="p-6" data-testid="access-denied">
        <h1 className="text-lg font-semibold">Access</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Only the app’s admins manage its people. {denied}
        </p>
      </div>
    );

  const path = (email: string) => ({ params: { path: { email } } });

  return (
    <div className="flex flex-col gap-6 p-6" data-testid="access-page">
      <div>
        <h1 className="text-lg font-semibold">Access</h1>
        <p className="text-muted-foreground text-sm">
          Who may use this app, their role, and which tenants each person sees.
        </p>
      </div>
      {error && (
        <p role="alert" className="text-destructive text-sm" data-testid="access-error">
          {error}
        </p>
      )}
      {notice && (
        <p className="text-sm" data-testid="access-notice">
          {notice}
        </p>
      )}
      {!directory.available && (
        <p className="text-muted-foreground text-sm" data-testid="access-directory-note">
          {directory.note ?? 'People are managed in this app’s identity provider.'}
        </p>
      )}

      <section className="flex flex-col gap-3" aria-labelledby="access-people">
        <h2 id="access-people" className="font-medium">
          People
        </h2>
        {directory.available && (
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              const email = invite.email.trim();
              if (!email) return;
              void act(
                () =>
                  client.POST('/people', { body: { email, role: invite.role, ...levelBody(invite.level) } }),
                `${email} is invited.`,
              ).then((ok) => ok && setInvite({ email: '', role: invite.role, level: invite.level }));
            }}
          >
            <Input
              aria-label="Email"
              data-testid="access-invite-email"
              type="email"
              placeholder="name@company.com"
              className="w-64"
              value={invite.email}
              onChange={(e) => setInvite({ ...invite, email: e.target.value })}
            />
            <select
              aria-label="Role"
              data-testid="access-invite-role"
              className={selectClass}
              value={invite.role}
              onChange={(e) => setInvite({ ...invite, role: e.target.value as Role })}
            >
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <LevelSelect
              label="Tenants"
              testId="access-invite-tenant"
              tenants={tenants}
              value={invite.level}
              onChange={(level) => setInvite({ ...invite, level })}
            />
            <Button type="submit" disabled={busy || !invite.email.trim()} data-testid="access-invite-submit">
              Invite
            </Button>
          </form>
        )}
        {linked && (
          <p className="text-muted-foreground text-xs">
            All tenants means admin: an admin sees every tenant, a member or viewer sees one tenant.
          </p>
        )}
        {people === null ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : people.length === 0 ? (
          <p className="text-muted-foreground text-sm" data-testid="access-people-empty">
            Nobody yet.
          </p>
        ) : (
          <table className="w-full text-sm" data-testid="access-people">
            <thead>
              <tr className="border-b text-left">
                <th className="py-2 font-medium">Person</th>
                <th className="py-2 font-medium">Role</th>
                <th className="py-2 font-medium">Tenants</th>
                <th className="py-2 font-medium">Sign-in</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {people.map((p) => {
                const key = idOf(p.email);
                const locked = busy || !p.changeable;
                return (
                  <tr
                    key={p.email}
                    className="border-b"
                    data-testid={`access-person-${key}`}
                    data-email={p.email}
                  >
                    <td className="py-2">
                      <div>{p.email}</div>
                      {p.name && <div className="text-muted-foreground text-xs">{p.name}</div>}
                    </td>
                    <td className="py-2">
                      <select
                        aria-label={`Role of ${p.email}`}
                        data-testid={`access-role-${key}`}
                        className={selectClass}
                        value={p.role}
                        disabled={locked}
                        onChange={(e) =>
                          void act(
                            () =>
                              client.PATCH('/people/{email}', {
                                ...path(p.email),
                                body: { role: e.target.value as Role },
                              }),
                            `${p.email} is now ${e.target.value}.`,
                          )
                        }
                      >
                        {ROLES.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2">
                      <LevelSelect
                        label={`Tenants of ${p.email}`}
                        testId={`access-level-${key}`}
                        tenants={tenants}
                        value={levelValue(p)}
                        disabled={locked}
                        onChange={(v) =>
                          void act(
                            () => client.PATCH('/people/{email}', { ...path(p.email), body: levelBody(v) }),
                            `${p.email}’s tenants are changed.`,
                          )
                        }
                      />
                    </td>
                    <td className="py-2">
                      <Badge
                        variant={p.status === 'active' ? 'secondary' : 'outline'}
                        data-testid={`access-status-${key}`}
                      >
                        {STATUS[p.status]}
                      </Badge>
                    </td>
                    <td className="flex justify-end gap-2 py-2">
                      {(p.status === 'invited' || p.status === 'expired') && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={locked}
                          data-testid={`access-resend-${key}`}
                          onClick={() =>
                            void act(
                              () => client.POST('/people/{email}/resend', path(p.email)),
                              `A new invitation is on its way to ${p.email}.`,
                            )
                          }
                        >
                          Resend invitation
                        </Button>
                      )}
                      {removing === p.email ? (
                        <>
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={busy}
                            data-testid={`access-confirm-remove-${key}`}
                            onClick={() =>
                              void act(
                                () => client.DELETE('/people/{email}', path(p.email)),
                                `${p.email} no longer has access.`,
                              ).then(() => setRemoving(null))
                            }
                          >
                            Remove access
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setRemoving(null)}>
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={locked}
                          data-testid={`access-remove-${key}`}
                          onClick={() => setRemoving(p.email)}
                        >
                          Remove
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <section className="flex flex-col gap-3" aria-labelledby="access-tenants">
        <h2 id="access-tenants" className="font-medium">
          Tenants
        </h2>
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const name = tenantName.trim();
            if (!name) return;
            void act(() => client.POST('/tenants', { body: { name } }), `Tenant ${name} is created.`).then(
              (ok) => ok && setTenantName(''),
            );
          }}
        >
          <Input
            aria-label="Tenant name"
            data-testid="access-tenant-name"
            placeholder="Tenant name"
            className="w-64"
            value={tenantName}
            onChange={(e) => setTenantName(e.target.value)}
          />
          <Button
            type="submit"
            variant="outline"
            disabled={busy || !tenantName.trim()}
            data-testid="access-tenant-create"
          >
            Create tenant
          </Button>
        </form>
        {tenants.length > 0 && (
          <ul className="flex flex-col gap-1 text-sm" data-testid="access-tenants">
            {tenants.map((t) => (
              <li key={t.id} className="flex items-center gap-2" data-testid={`access-tenant-${idOf(t.id)}`}>
                {renaming?.id === t.id ? (
                  <form
                    className="flex items-center gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const name = renaming.name.trim();
                      if (!name) return;
                      void act(
                        () =>
                          client.PATCH('/tenants/{id}', { params: { path: { id: t.id } }, body: { name } }),
                        `Tenant renamed to ${name}.`,
                      ).then(() => setRenaming(null));
                    }}
                  >
                    <Input
                      aria-label={`New name of ${t.name}`}
                      className="h-8 w-56"
                      value={renaming.name}
                      onChange={(e) => setRenaming({ id: t.id, name: e.target.value })}
                    />
                    <Button size="sm" type="submit" disabled={busy}>
                      Save
                    </Button>
                    <Button size="sm" variant="ghost" type="button" onClick={() => setRenaming(null)}>
                      Cancel
                    </Button>
                  </form>
                ) : (
                  <>
                    <span>{t.name}</span>
                    <span className="text-muted-foreground text-xs">
                      {t.id} · {t.people} {t.people === 1 ? 'person' : 'people'}
                    </span>
                    <Button size="sm" variant="ghost" onClick={() => setRenaming({ id: t.id, name: t.name })}>
                      Rename
                    </Button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            data-testid="access-linked"
            checked={linked}
            disabled={busy}
            onChange={(e) =>
              void act(
                () =>
                  client.PUT('/settings', { body: { allTenantsRole: e.target.checked ? 'admin' : null } }),
                e.target.checked ? 'All tenants now means admin.' : 'Roles and tenants are now independent.',
              )
            }
          />
          All tenants means admin
        </label>
      </section>
    </div>
  );
}
